import {BaseApiClient} from './base-api';

/**
 * API client that supports both dependency injection and self-sufficient auth.
 *
 * This is the RECOMMENDED pattern for new features.
 *
 * Auth strategy (priority order):
 * 1. Constructor token (for testing/SSR) - if provided, used directly
 * 2. Dynamic fetch via getAuthHeaders() - automatic fallback for browser usage
 *
 * Benefits:
 * - Self-sufficient: Clients work without explicit token passing
 * - Testable: Inject mock token via constructor
 * - SSR-compatible: Token injection for server-side rendering
 * - No stale closures: Token fetched at request time, not at hook call time
 *
 * @example
 * ```typescript
 * // Production (no token needed - self-sufficient)
 * const client = new AuthenticatedApiClient();
 * const data = await client.request('/api/documents');
 *
 * // Testing (inject mock token)
 * const client = new AuthenticatedApiClient('mock-token');
 * ```
 */
export class AuthenticatedApiClient extends BaseApiClient {
  private readonly token?: string;

  constructor(token?: string, baseUrl?: string) {
    super(baseUrl);
    this.token = token;
  }

  /**
   * Override prepareHeaders to inject auth token.
   *
   * Priority:
   * 1. Constructor token (for testing/SSR)
   * 2. Dynamic fetch via getAuthHeaders() (browser runtime)
   */
  protected async prepareHeaders(options: RequestInit): Promise<Headers> {
    const headers = new Headers(options.headers ?? {});

    if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    // Priority 1: Constructor token (for testing/SSR)
    if (this.token) {
      headers.set('Authorization', `Bearer ${this.token}`);
      return headers;
    }

    // Priority 2: Dynamic fetch via getAuthHeaders()
    try {
      const {getAuthHeaders} = await import('../auth/oidc-token');
      const authHeaders = await getAuthHeaders();
      for (const [key, value] of Object.entries(authHeaders)) {
        headers.set(key, value);
      }
    } catch (error) {
      console.warn(
        'AuthenticatedApiClient: Failed to get auth headers:',
        error
      );
    }

    return headers;
  }
}
